import {
    useQuery,
    UseQueryResult,
    keepPreviousData as previousPlaceholder,
} from '@tanstack/react-query';

import {
    AuthorStatsSummaryQuery,
    StatsSummaryResponse,
    StatsResponse,
    AttributionsStatsSummaryQuery,
} from '../lib/api/Stats';

import { useApi } from '../contexts/ApiContext';
import { useSubscriptions } from '../contexts/SiteContext';

const labels: Record<StatsType, string> = {
    document_start: 'Vues',
    pageview: 'Vues',
    document_complete: 'Complétés',
    subscribe: 'Abonnements',
};

export interface Stats {
    id: StatsType;
    label: string;
    count: number;
}

export interface StatsSummary {
    total: Stats[];
    dates: {
        date: string;
        stats: Stats[];
    }[];
    documents: Array<{
        document: DocumentSnippet;
        stats: Stats[];
    }>;
}

type UseStatsSummaryResult = {
    summary?: StatsSummary;
} & UseQueryResult<StatsSummaryResponse>;

interface StatsHighlightsItem {
    label: string;
    count: number;
    items: Array<{
        date: string;
        count: number;
    }>;
}

export function useStatsLabels(): Record<StatsType, string> {
    const subscriptions = useSubscriptions();
    const allLabels = {
        ...labels,
        ...subscriptions.reduce(
            (acc, { handle, name }) => ({
                ...acc,
                [`subscribe:${handle}`]: `Abonnements ${name}`,
            }),
            {},
        ),
    };
    return allLabels;
}

export function useFormatStats() {
    const allLabels = useStatsLabels();
    return (stats: StatsResponse | null): Stats[] => {
        const newStats = Object.keys(stats || {})
            .filter((key) => key !== 'subscribe:micromag' && key !== 'subscribe')
            .reduce(
                (acc, key) => [
                    ...acc,
                    {
                        id: key,
                        label: allLabels[key] || key,
                        count: stats[key],
                    },
                ],
                [],
            );
        return [
            ...newStats,
            {
                id: 'subscribe',
                label: allLabels['subscribe'],
                count: Object.keys(stats)
                    .filter((key) => key.match(/^subscribe:/) && key !== 'subscribe:micromag')
                    .reduce((acc, key) => acc + stats[key], 0),
            },
        ];
    };
}

export function useHighlightsFromSummary(
    summary: StatsSummary | null,
    days: number = 7,
    stats: StatsType[] = ['pageview', 'document_complete', 'subscribe'],
): StatsHighlightsItem[] {
    const labels = useStatsLabels();
    const { dates = [] } = summary || {};
    return stats.map((highlight) => ({
        label: labels[highlight] || highlight,
        count: dates
            .slice(-days)
            .reduce(
                (acc, { stats }) =>
                    stats.reduce(
                        (acc, { id, count }) => (id === highlight ? acc + count : acc),
                        acc,
                    ),
                0,
            ),
        items: dates.slice(-days).map(({ date, stats }) => ({
            date,
            count: stats.reduce((acc, { id, count }) => (id === highlight ? acc + count : acc), 0),
        })),
    }));
}

export function useAuthorStatsSummary(
    authorId: string | null,
    query: AuthorStatsSummaryQuery = null,
    opts = null,
): UseStatsSummaryResult {
    const api = useApi();
    const formatStats = useFormatStats();
    const { keepPreviousData = true, enabled = true, ...otherOpts } = opts || {};
    const { data = null, ...queryResult } = useQuery<
        StatsSummaryResponse,
        Error,
        StatsSummaryResponse,
        ['author_stats_summary', string, AuthorStatsSummaryQuery | null]
    >({
        queryKey: ['author_stats_summary', authorId, query],
        queryFn: ({ queryKey: [, authorIdParam, queryParam], signal }) =>
            api.stats.authorSummary(authorIdParam, queryParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        enabled: enabled && authorId !== null,
        ...otherOpts,
    });

    if (data === null) {
        return {
            data,
            ...queryResult,
        };
    }

    const { total, dates = {}, documents = [] } = data;

    return {
        summary: {
            total: formatStats(total),
            dates: Object.keys(dates).reduce(
                (acc, date) => [
                    ...acc,
                    {
                        date,
                        stats: formatStats(dates[date]),
                    },
                ],
                [],
            ),
            documents: documents.map(({ document, stats }) => ({
                document,
                stats: formatStats(stats),
            })),
        },
        data,
        ...queryResult,
    };
}

interface UseAuthorStatsHighlightsOpts {
    stats?: StatsType[];
    [key: string]: unknown;
}

export function useAuthorStatsHighlights(
    authorId: string | null,
    days: number = 7,
    opts: UseAuthorStatsHighlightsOpts = null,
) {
    const { stats = ['pageview', 'document_complete', 'subscribe'], ...queryOpts } = opts || {};
    const { summary, ...queryResult } = useAuthorStatsSummary(
        authorId,
        {
            // days,
        },
        {
            ...queryOpts,
        },
    );
    const highlights = useHighlightsFromSummary(summary || null, days, stats);
    return { highlights, ...queryResult };
}

export function useAttributionsStatsSummary(
    query: AttributionsStatsSummaryQuery = null,
    opts = null,
): UseStatsSummaryResult {
    const api = useApi();
    const formatStats = useFormatStats();
    const { keepPreviousData = true } = opts || {};
    const { data = null, ...queryResult } = useQuery<
        StatsSummaryResponse,
        Error,
        StatsSummaryResponse,
        ['attributions', AttributionsStatsSummaryQuery]
    >({
        queryKey: ['attributions', query],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.stats.attributionsSummary(queryParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    if (data === null) {
        return {
            data,
            ...queryResult,
        };
    }

    const { total, dates = {}, documents = [] } = data;

    return {
        summary: {
            total: formatStats(total),
            dates: Object.keys(dates).reduce(
                (acc, date) => [
                    ...acc,
                    {
                        date,
                        stats: formatStats(dates[date]),
                    },
                ],
                [],
            ),
            documents: documents.map(({ document, stats }) => ({
                document,
                stats: formatStats(stats),
            })),
        },
        data,
        ...queryResult,
    };
}

export function useAttributionStatsHighlights(days: number = 28, opts = null) {
    const { stats = ['subscribe', 'document_start', 'document_complete'], ...otherOpts } =
        opts || {};
    const { summary, ...queryResult } = useAttributionsStatsSummary(
        {
            days,
        },
        otherOpts,
    );
    const highlights = useHighlightsFromSummary(summary || null, days, stats);
    return { highlights, ...queryResult };
}
